<template>
    <v-menu
        v-model="open"
        offset-y
        nudge-bottom="4"
        eager
        :close-on-content-click="false"
        :close-on-click="closeOnClick"
        :content-class="
            wide ? 'dd-filter-menu dd-filter-menu--wide' : 'dd-filter-menu'
        "
        :min-width="minWidth"
    >
        <template #activator="{ on, attrs }">
            <v-btn
                :class="[
                    'dd-filter-pill',
                    {
                        'dd-filter-pill--open': open,
                        'dd-filter-pill--active': active,
                    },
                ]"
                v-bind="attrs"
                v-on="on"
            >
                {{ text }}
                <v-icon right>{{
                    open ? 'expand_less' : 'expand_more'
                }}</v-icon>
            </v-btn>
        </template>
        <div class="dd-filter-menu__body" @keydown.enter="applyAfterInput">
            <slot :value="draft" :input="setDraft"></slot>
        </div>
        <div class="dd-filter-menu__actions">
            <v-btn color="primary" :block="!wide" @click="apply">{{
                applyText
            }}</v-btn>
        </div>
    </v-menu>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'

// One filter as a DoorDash filter pill (merchant portal Orders: "Order status ⌄"): the menu edits
// a draft of the list filters and only "Apply" hands it to the list, closing it drops the draft
// (tasks#211).
export default {
    props: {
        value: {
            type: Object,
            required: true,
        },
        field: {
            type: Object,
            required: true,
        },
        // a menu whose input opens a popup outside it (Google address suggestions) stays open on an
        // outside click, otherwise picking a suggestion would close it unapplied
        closeOnClick: {
            type: Boolean,
            default: true,
        },
        // unset: as wide as the pill, growing with its options (merchant portal Orders)
        minWidth: {
            type: [Number, String],
            default: undefined,
        },
        // a calendar menu: the Apply button on the right of a divided footer (Transactions)
        wide: {
            type: Boolean,
        },
        // the value comes from an active quick-filter chip ("Upcoming"), so the pill itself is
        // not shown as applied
        ownedByChip: {
            type: Boolean,
        },
        // overrides the applied look derived from the field's value: a pill whose value is always set
        // (the restaurant finder's address / meal / day) is applied only while it differs from its
        // default (tasks#222)
        applied: {
            type: Boolean,
            default: null,
        },
    },
    data() {
        return {
            open: false,
            draft: cloneDeep(this.value),
        }
    },
    computed: {
        fieldValue() {
            const key =
                this.field.inputType === 'location'
                    ? this.field.other?.key
                    : this.field.key
            const value = this.value[key]
            return this.field.type && this.field.inputType !== 'location'
                ? value?.[this.field.type]
                : value
        },
        // a date range keeps both ends; the list can also come with one end only
        rangeEnd() {
            const end = this.field.end
            if (this.field.inputType !== 'daterange' || !end) return undefined
            return end.type
                ? this.value[end.key]?.[end.type]
                : this.value[end.key]
        },
        active() {
            if (this.applied !== null) return this.applied
            if (this.ownedByChip) return false
            const value = this.fieldValue
            if (this.rangeEnd) return true
            if (Array.isArray(value)) return value.length > 0
            return value !== undefined && value !== null && value !== ''
        },
        text() {
            const label =
                this.field.translatedLabel || this.$t(this.field.label)
            const value = this.fieldValue
            if (this.field.inputType === 'daterange' && this.active) {
                const format = (date) => this.$date(date).format('D MMM')
                const end = this.rangeEnd
                if (!value) return `${label}: until ${format(end)}`
                if (!end) return `${label}: from ${format(value)}`
                return format(value) === format(end)
                    ? `${label}: ${format(value)}`
                    : `${label}: ${format(value)} – ${format(end)}`
            }
            return Array.isArray(value) && value.length
                ? `${label} (${value.length})`
                : label
        },
        applyText() {
            return this.$te('actions.apply')
                ? this.$t('actions.apply')
                : 'Apply'
        },
    },
    watch: {
        open(open) {
            if (open) this.draft = cloneDeep(this.value)
        },
        value: {
            deep: true,
            handler(value) {
                if (!this.open) this.draft = cloneDeep(value)
            },
        },
    },
    methods: {
        setDraft(value) {
            this.draft = value
        },
        apply() {
            this.$emit('input', cloneDeep(this.draft))
            this.open = false
        },
        // a text field writes the draft from a watcher, one tick after the key press
        applyAfterInput() {
            this.$nextTick(this.apply)
        },
    },
}
</script>
